import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

function GridPagination({
  pageIndex = 0,
  pageSize = 10,
  hasNextPage = false,
  isLoading = false,
  pageSizeOptions = [10, 20, 30, 40, 50],
  onPaginationChange = () => {},
  className = "",
  ...props
} = {}) {
  // Handle page navigation
  const handlePageChange = (newPageIndex) => {
    // We can only navigate to valid page indexes (0 or higher)
    if (newPageIndex >= 0) {
      // If going forward, we need to check if there's a next page
      if (newPageIndex > pageIndex && !hasNextPage) {
        return; // Don't allow going to next page if there isn't one
      }
      onPaginationChange({pageIndex:newPageIndex, pageSize:pageSize});
    }
  };
  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    // Calculate the current offset (position in the data)
    const currentOffset = pageIndex * pageSize;
    
    // Calculate the new page index that would maintain a similar position
    const newPageIndex = Math.floor(currentOffset / newSize);
    
    // Update pagination with both new values
    onPaginationChange({ pageIndex: newPageIndex, pageSize: newSize });
  };

  return (
    <div
      className={cn(
        "flex w-full flex-col-reverse items-center justify-between gap-4 overflow-auto p-1 sm:flex-row sm:gap-8",
        className,
      )}
      {...props}
    >
      <div className="flex-1">
        {/* Empty space or other info */}
      </div>
      <div className="flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8">
        <div className="flex items-center space-x-2">
          {isLoading && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
          <p className="whitespace-nowrap font-medium text-sm">Rows per page</p>
          <Select
            value={`${pageSize}`}
            onValueChange={(value) => {
              handlePageSizeChange(Number(value));
            }}
            disabled={isLoading}
          >
            <SelectTrigger className="h-8 w-20 [&[data-size]]:h-8">
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={`${size}`}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-center font-medium text-sm">
          Page {pageIndex + 1}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            aria-label="Go to previous page"
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => handlePageChange(pageIndex - 1)}
            disabled={isLoading || pageIndex === 0}
          >
            <ChevronLeft />
          </Button>
          <Button
            aria-label="Go to next page"
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => handlePageChange(pageIndex + 1)}
            disabled={isLoading || !hasNextPage}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default GridPagination;